import {
  INonFlyingAssignment,
  IDisplayPairing,
  IViolationResult,
  IDisplayBidPairing,
  IDisplayBidNFA,
} from "@vmosolutions/oc-interfaces";
import { Camera } from "../../SharedGL/Camera";
import { CrewSchedEvent } from "src/redux/reducers/CrewScheduleReducer";

/* Finding closest flight index */

export interface IRGBA {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

/**
 * getClosestIndex helper function to find which index is closer to the target
 * @param flightsthe - array of flights to search for closest flight
 * @param firstIndex - the first index to compare if it's closer to target
 * @param secondIndex - the second index to compare against to see which is closer
 * @param target - the target time (in millis) to search for
 * @param comparePropthe - property to actaully compare. Either departure or arrival
 * @returns number Either firstIndex or secondIndex; whichever is closer to the target
 */
const getClosestIndex = (
  event: any[],
  firstIndex: number,
  secondIndex: number,
  target: number,
  compareProp: EventTimes
): number => {
  const val1 = new Date(event[firstIndex][compareProp]).getTime();
  const val2 = new Date(event[secondIndex][compareProp]).getTime();
  return target - val1 >= val2 - target ? secondIndex : firstIndex;
};

export type EventTimes =
  | "actualOff"
  | "actualOn"
  | "actualStart"
  | "actualEnd"
  | "start"
  | "end"
  | "period.end"
  | "period.start"
  | "date";
/**
 * findClosestIndex finds the closest flight with scheduledOff close to the target time
 * @param event - the array of crew events to search for closest event
 * @param target - the target time (in millis) to search for
 * @param compareProp - the property to actaully compare. Either departure or arrival
 * @returns index returns the index of the event that has closest time to the target
 */
export const findClosestIndex = (
  event: (IDisplayPairing | INonFlyingAssignment | IViolationResult | IDisplayBidPairing | IDisplayBidNFA)[],
  target: number,
  compareProp: EventTimes
): number => {
  const length = event.length;

  if (!target || length === 0) return -1;

  // Corner cases
  ///@ts-ignore  requires rewrite of the timeline data.
  if (target <= new Date(event[0][compareProp]).getTime()) return 0;
  ///@ts-ignore  requires rewrite of the timeline data.
  if (target >= new Date(event[length - 1][compareProp]).getTime()) return length - 1;

  // Doing binary search
  let i = 0,
    j = length,
    mid = 0;
  while (i < j) {
    mid = Math.floor((i + j) / 2);

    ///@ts-ignore  requires rewrite of the timeline data.
    const midTime = new Date(event[mid][compareProp]).getTime();
    if (target === midTime) return mid;

    /* If target is less than array element, 
        then search in left */
    if (target < midTime) {
      // If target is greater than previous
      // to mid, return closest of two
      ///@ts-ignore  requires rewrite of the timeline data.
      const previousTime = new Date(event[mid - 1][compareProp]).getTime();
      if (mid > 0 && target > previousTime) return getClosestIndex(event, mid - 1, mid, target, compareProp);

      /* Repeat for left half */
      j = mid;
    }
    // If target is greater than mid
    else {
      ///@ts-ignore  requires rewrite of the timeline data.
      const nextTime = new Date(event[mid + 1][compareProp]).getTime();
      if (mid < length - 1 && target < nextTime) {
        return getClosestIndex(event, mid, mid + 1, target, compareProp);
      }
      i = mid + 1; // update i
    }
  }

  // Only single element left after search
  return mid;
};

export interface IIndexRange {
  minIndex: number;
  maxIndex: number;
}

//This can get removed when we start calculating overlaps on the backend -start
export const getNFAsInRangeByRange = (nonFlyingAssignments: INonFlyingAssignment[], range: number[]): IIndexRange => {
  const [minTime, maxTime] = range;
  const closestStartIndex: number = findClosestIndex(nonFlyingAssignments, minTime, "end");

  let indexesToTest = closestStartIndex > 5 ? 5 : closestStartIndex;
  //make sure that there are not nonFlyingAssignments in max 5 preceeding nonFlyingAssignments that may end after this pairing
  while (
    indexesToTest > 0 &&
    new Date(nonFlyingAssignments[closestStartIndex - indexesToTest]?.["end"]).getTime() < minTime
  ) {
    indexesToTest--;
  }
  const doubleCheckedClosestStartIndex =
    closestStartIndex - indexesToTest > 0 ? closestStartIndex - indexesToTest : closestStartIndex;
  const closestEndIndex: number = findClosestIndex(nonFlyingAssignments, maxTime, "start");
  return { minIndex: doubleCheckedClosestStartIndex, maxIndex: closestEndIndex };
};

export const getPairingsInRangeByRange = (pairings: IDisplayPairing[], range: number[]): IIndexRange => {
  const [minTime, maxTime] = range;
  const closestStartIndex: number = findClosestIndex(pairings, minTime, "end");
  let indexesToTest = closestStartIndex > 5 ? 5 : closestStartIndex;
  //make sure that there are not pairings in max 5 preceeding pairings that may end after this pairing
  while (indexesToTest > 0 && new Date(pairings[closestStartIndex - indexesToTest]?.["end"]).getTime() < minTime) {
    indexesToTest--;
  }
  const doubleCheckedClosestStartIndex =
    closestStartIndex - indexesToTest > 0 ? closestStartIndex - indexesToTest : closestStartIndex;
  const closestEndIndex: number = findClosestIndex(pairings, maxTime, "start");
  return { minIndex: doubleCheckedClosestStartIndex, maxIndex: closestEndIndex };
};

//This can get removed when we start calculating overlaps on the backend -end

export const getNFAsInRange = (
  camera: Camera,
  nonFlyingAssignments: (INonFlyingAssignment | IDisplayBidNFA)[]
): IIndexRange => {
  const [minTime, maxTime] = camera.getXRange();
  const closestStartIndex: number = findClosestIndex(nonFlyingAssignments, minTime, "end");

  //HACK you can have a nonFlyingAssignments with an index less than closestStartIndex that ends after this index
  let indexesToTest = closestStartIndex > 5 ? 5 : closestStartIndex;
  //make sure that there are not nonFlyingAssignments in max 5 preceeding nonFlyingAssignments that may end after this pairing
  while (
    indexesToTest > 0 &&
    new Date(nonFlyingAssignments[closestStartIndex - indexesToTest]?.["end"]).getTime() < minTime
  ) {
    indexesToTest--;
  }
  const doubleCheckedClosestStartIndex =
    closestStartIndex - indexesToTest > 0 ? closestStartIndex - indexesToTest : closestStartIndex;
  const closestEndIndex: number = findClosestIndex(nonFlyingAssignments, maxTime, "start");
  return { minIndex: doubleCheckedClosestStartIndex, maxIndex: closestEndIndex };
};

export const getPairingsInRange = (camera: Camera, pairings: (IDisplayPairing | IDisplayBidPairing)[]): IIndexRange => {
  const [minTime, maxTime] = camera.getXRange();
  const closestStartIndex: number = findClosestIndex(pairings, minTime, "end");
  //HACK you can have a pairing with an index less than closestStartIndex that ends after this index
  let indexesToTest = closestStartIndex > 5 ? 5 : closestStartIndex;
  //make sure that there are not pairings in max 5 preceeding pairings that may end after this pairing
  while (indexesToTest > 0 && new Date(pairings[closestStartIndex - indexesToTest]?.["end"]).getTime() < minTime) {
    indexesToTest--;
  }
  const doubleCheckedClosestStartIndex =
    closestStartIndex - indexesToTest > 0 ? closestStartIndex - indexesToTest : closestStartIndex;
  const closestEndIndex: number = findClosestIndex(pairings, maxTime, "start");
  return { minIndex: doubleCheckedClosestStartIndex, maxIndex: closestEndIndex };
};

export const getPlannedEventsInRange = (camera: Camera, plannedEvents: CrewSchedEvent[]): IIndexRange => {
  const [minTime, maxTime] = camera.getXRange();
  const closestStartIndex: number = findClosestIndex(plannedEvents, minTime, "end");

  const closestEndIndex: number = findClosestIndex(plannedEvents, maxTime, "start");

  return { minIndex: closestStartIndex, maxIndex: closestEndIndex };
};

export const getViolationsInRange = (camera: Camera, violations: IViolationResult[]): IIndexRange => {
  const [minTime, maxTime] = camera.getXRange();
  const closestStartIndex: number = findClosestIndex(violations, minTime, "period.end");

  const closestEndIndex: number = findClosestIndex(violations, maxTime, "period.start");

  return { minIndex: closestStartIndex, maxIndex: closestEndIndex };
};

export const getDayLogicModifiersInRange = (camera: Camera, violations: IViolationResult[]): IIndexRange => {
  const [minTime, maxTime] = camera.getXRange();
  const closestStartIndex: number = findClosestIndex(violations, minTime, "date");

  const closestEndIndex: number = findClosestIndex(violations, maxTime, "date");

  return { minIndex: closestStartIndex, maxIndex: closestEndIndex };
};

export interface ICrewRowSettings {
  crewRowHeight: number;
}
export const defaultCrewRowSettings: ICrewRowSettings = {
  //rowHeightPercentage: 1,
  crewRowHeight: 36 + 6,
  //crewRowPadding: 1,
  //actualDepartureStart: 6 + 6,
  //actualDepartureHeight: 20,
  //scheduledStart: 2,
  //scheduledHeight: 4 + 6,
  //crewRowTotalHeight: 36 + 6 + 1,
};
